import { useState } from "react";
import { Backdrop, Portal, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { LinkPreview, Step, StepLink } from "./decommissionSteps";

type Props = {
    number: number;
    step: Step;
    isCurrent: boolean;
    onAction?: () => void;
};

function LinkPreviews({ previews }: { previews: LinkPreview[] }) {
    const isSequence = previews.length > 1;

    return (
        <div className={`decommission-step__previews${isSequence ? "" : " decommission-step__previews--single"}`}>
            {previews.map(({ image, width, height, caption }, index) => (
                <figure className="decommission-step__previewItem" key={image}>
                    <img src={image} width={width} height={height} alt={caption} />
                    <figcaption>
                        {isSequence && <span className="decommission-step__previewNumber">{index + 1}</span>}
                        {caption}
                    </figcaption>
                </figure>
            ))}
        </div>
    );
}

// While the previews are open the page is dimmed and the link is raised above the backdrop
function StepLinkAnchor({ link }: { link: StepLink }) {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const anchor = (
        <a
            className={`decommission-step__link${isPreviewOpen ? " decommission-step__link--focused" : ""}`}
            href={link.href}
            target="_blank"
            rel="noreferrer"
        >
            {link.label} <OpenInNewIcon />
        </a>
    );

    if (!link.previews) return anchor;

    return (
        <>
            <Tooltip
                title={<LinkPreviews previews={link.previews} />}
                placement="left"
                open={isPreviewOpen}
                onOpen={() => setIsPreviewOpen(true)}
                onClose={() => setIsPreviewOpen(false)}
                classes={{ tooltip: "decommission-step__preview" }}
            >
                {anchor}
            </Tooltip>

            <Portal>
                <Backdrop open={isPreviewOpen} className="decommission-step__backdrop" />
            </Portal>
        </>
    );
}

function StepAside({ step, onAction }: Pick<Props, "step" | "onAction">) {
    const { state, link, action } = step;

    if (state === "checking") return <span className="decommission-step__note">Checking…</span>;

    if (state === "done") {
        return <span className="decommission-step__note decommission-step__note--done">{step.doneNote}</span>;
    }

    if (state === "locked") {
        return (
            <span className="decommission-step__note">
                <LockOutlinedIcon /> {step.lockedReason}
            </span>
        );
    }

    if (link) return <StepLinkAnchor link={link} />;

    if (action && onAction) {
        return (
            <button type="button" className={`decommission-step__action decommission-step__action--${action.tone}`} onClick={onAction}>
                {action.label}
            </button>
        );
    }

    return null;
}

export default function DecommissionStep({ number, step, isCurrent, onAction }: Props) {
    const modifiers = `decommission-step--${step.state}${isCurrent ? " decommission-step--current" : ""}`;

    return (
        <li className={`decommission-step ${modifiers}`}>
            <span className="decommission-step__marker">
                {step.state === "done" ? <CheckIcon /> : number}
            </span>

            <div className="decommission-step__card">
                <div className="decommission-step__summary">
                    <div className="decommission-step__heading">
                        <p className="decommission-step__title">{step.title}</p>
                        <span className={`decommission-step__badge decommission-step__badge--${step.kind}`}>
                            {step.kind}
                        </span>
                    </div>
                    <p className="decommission-step__detail">{step.detail}</p>
                    {step.warning && <p className="decommission-step__warning">{step.warning}</p>}
                </div>

                <div className="decommission-step__aside">
                    <StepAside step={step} onAction={onAction} />
                </div>

                {isCurrent && <p className="decommission-step__description">{step.description}</p>}
            </div>
        </li>
    );
}
